// GA4 event layer. Everything funnels through track(), which no-ops when the
// gtag snippet in index.html is absent (ad blockers, headless tests) and
// mirrors to the console on localhost so an event can be checked without
// opening GA4 DebugView.
//
// Modes are timed. A mode_enter fires on entry, a mode_time segment fires
// whenever the tab is hidden or the mode ends (so an abandoned tab still
// reports what it had), and a mode_exit with the total visible seconds fires
// on a real exit. Sum mode_time.seconds for time in mode; count mode_enter
// against mode_exit for completion.

import { IS_APP, IS_QUEST_APP } from './appmode.js';

const LOCAL = typeof location !== 'undefined' && /^(localhost|127\.0\.0\.1)$/.test(location.hostname);

// mode -> { since: performance.now() of the current visible segment or null
//           while hidden, total: visible seconds banked so far, params }
const active = new Map();

// GA4 events that are also first-party funnel steps and experiment metrics
// (window.ossAb from the analytics snippet, src/experiments/runtime.js).
const AB_EVENTS = { view_item: 'product_view', begin_checkout: 'begin_checkout' };

// Count events for the funnel and the visitor's experiments, once per page
// view each. `urgent` sends at once, for an event the page is leaving on.
export function abCount(events, urgent = false) {
  if (IS_APP || typeof window === 'undefined' || !window.ossAb) return;
  try { window.ossAb.count(events, urgent); } catch { /* never let counting break the app */ }
}

// A step of a funnel the page's counter does not carry (SIGNUPS and PRO in
// src/experiments/registry.js), posted to /api/ab on its own. `once`
// counts it at most once a page view.
const stepped = new Set();
export function countStep(event, { once = false } = {}) {
  if (IS_APP || typeof fetch === 'undefined') return;
  if (once) {
    if (stepped.has(event)) return;
    stepped.add(event);
  }
  try {
    fetch('/api/ab', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ exp: '_site', arm: 'all', event }),
      credentials: 'same-origin',
      keepalive: true,
    }).catch(() => {});
  } catch { /* no fetch: nothing to count with */ }
}

// Astronomy Pro's steps by where the offer was seen (PRO and PRO_PAGES in
// src/experiments/registry.js): 'astro' when Astronomy mode showed it
// (src/astro/pro.js sends placements astro-<control>), 'page' anywhere else.
// The upgrade sheet counts the first two steps by it and sends it through
// Checkout (from=) to the thanks page, so a checkout stays in one funnel.
export const proOrigin = (placement) => (String(placement || '').startsWith('astro-') ? 'astro' : 'page');
export const PRO_STEP = {
  view: { astro: 'pro_upgrade_view', page: 'pro_page_view' },
  checkout: { astro: 'pro_checkout', page: 'pro_page_checkout' },
  started: { astro: 'pro_started', page: 'pro_page_started' },
};

// Whether this page view counted viewer_open, and then viewer_engaged.
let openedFromHome = false;
let engaged = false;

// The viewer has loaded (its loading screen is done). GA4 hears viewer_ready
// on every load, with from_home (a same-site referrer at /, so no storage is
// needed to tell) and the seconds since the page started: the funnel's step
// after a hero click, read in GA4 since 28 October 2026, and who leaves
// during the loading screen. viewer_open still reaches the first-party
// counter for an experiment that lists it.
export function viewerOpened() {
  if (typeof document === 'undefined') return;
  try {
    const from = new URL(document.referrer);
    openedFromHome = from.origin === location.origin && from.pathname === '/';
  } catch { /* no referrer */ }
  const loadS = typeof performance !== 'undefined' ? Math.round(performance.now() / 100) / 10 : undefined;
  track('viewer_ready', { from_home: openedFromHome ? 1 : 0, load_s: loadS });
  if (openedFromHome) abCount('viewer_open');
}

// A person exploring: choosing a body (select_content) or moving the clock
// (time_travel). Each `key` is sent once per page view, so a body picked
// again or the scrubber dragged twice adds nothing. The first one in a page
// view opened from the home page is viewer_engaged, which reaches the
// first-party counter only for an experiment that lists it (the funnel reads
// it in GA4 since 28 October 2026).
const explored = new Set();
export function explore(name, key, params = {}) {
  const id = `${name}:${key}`;
  if (explored.has(id)) return;
  explored.add(id);
  track(name, params);
  if (openedFromHome && !engaged) {
    engaged = true;
    abCount('viewer_engaged');
  }
}

export function track(name, params = {}) {
  // No analytics in the App Store apps (src/appmode.js).
  if (IS_APP) return;
  // Every event from the Meta Quest app says so.
  if (IS_QUEST_APP) params = { ...params, app: 'quest' };
  if (LOCAL) console.debug('[analytics]', name, params);
  if (AB_EVENTS[name]) abCount(AB_EVENTS[name], name === 'begin_checkout');
  const g = typeof window !== 'undefined' ? window.gtag : null;
  if (typeof g !== 'function') return;
  try { g('event', name, params); } catch { /* never let telemetry break the app */ }
}

function nowSec() { return performance.now() / 1000; }

function closeSegment(mode, entry, reason, transport) {
  if (entry.since == null) return 0;
  const seconds = Math.round(nowSec() - entry.since);
  entry.since = null;
  entry.total += seconds;
  if (seconds > 0) {
    track('mode_time', {
      mode, seconds, value: seconds, reason, ...entry.params,
      ...(transport ? { transport_type: transport } : {})
    });
  }
  return seconds;
}

export function modeEnter(mode, params = {}) {
  if (active.has(mode)) return;
  const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
  active.set(mode, { since: hidden ? null : nowSec(), total: 0, params });
  track('mode_enter', { mode, ...params });
}

export function modeExit(mode, params = {}) {
  const entry = active.get(mode);
  if (!entry) return;
  active.delete(mode);
  closeSegment(mode, entry, 'exit');
  track('mode_exit', { mode, seconds: entry.total, value: entry.total, ...entry.params, ...params });
}

export function modeActive(mode) { return active.has(mode); }

if (typeof document !== 'undefined') {
  // visibilitychange is the reliable signal on mobile, where pagehide and
  // beforeunload often never fire. Beacon transport survives the tab closing.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      for (const [mode, entry] of active) closeSegment(mode, entry, 'hidden', 'beacon');
    } else {
      const t = nowSec();
      for (const entry of active.values()) if (entry.since == null) entry.since = t;
    }
  });
}
